import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { useIntersectionObserver } from '@/core/hooks/useIntersectionObserver';
import goldIcon from '@/shared/assets/icons/gold.svg';
import diamondIcon from '@/shared/assets/icons/diamond.svg';
import solitairesIcon from '@/shared/assets/icons/solitaires.svg';
import toolsIcon from '@/shared/assets/icons/tools.svg';
import { cn } from '@/shared/utils/cn';
import { formatPdpCarat } from '@/features/pdp/utils/formatPdpCarat';
import { parseProductGemstones } from '@/features/pdp/utils/productDetailStones';
import { WeightInfoTooltip } from './WeightInfoTooltip';

interface ProductDimensionsProps {
  productCode: string | null;
  productHeight: number | null;
  productWidth: number | null;
  productLength: number | null;
  productWeight: number | null;
  metalWeight: number;
  diamondCarat: number | null;
  noOfDiamonds: number | null;
  diamondStoneType: string | null;
  solitaireCarat: number | null;
  solitaireClarity: string | null;
  solitaireColor: string | null;
  solitaireTotalNoOfStones: number | null;
  gemstones: string | null;
  gemstonesTypeAndCount: string | null;
  goldPurity: string | null;
  metal: string;
  onSectionViewed?: () => void;
  onInfoIconClick?: (label: string) => void;
}

function getMetalDisplayName(metal: string): string {
  return metal === 'Gold' ? 'Yellow Gold' : metal;
}

function DetailIcon({ src, className }: { src: string; className?: string }) {
  return (
    <img
      src={src}
      alt=""
      aria-hidden
      className={cn('mx-auto mb-[5px] block object-contain text-black', className)}
    />
  );
}

interface DetailValueProps {
  value: number | string;
  unit: string;
  label: string;
  tooltip: string;
}

function DetailValue({ value, unit, label, tooltip, onInfoIconClick }: DetailValueProps & { onInfoIconClick?: (label: string) => void }) {
  return (
    <div className="mx-auto flex w-full flex-col items-center text-center">
      <div className="mb-[3px] whitespace-nowrap border-b border-[#ededed] text-[16.8px] leading-[1.5em] tracking-[1px] max-[359px]:text-xs">
        {value} <span className="text-[13px] tracking-[0.7px]">{unit}</span>
      </div>
      <WeightInfoTooltip label={label} text={tooltip} onInfoClick={onInfoIconClick} />
    </div>
  );
}

interface TopDetailItemProps {
  className?: string;
  icon?: ReactNode;
  label?: ReactNode;
  children?: ReactNode;
}

function TopDetailItem({ className, icon, label, children }: TopDetailItemProps) {
  return (
    <div className={cn('relative float-left w-1/2 p-2.5 text-center', className)}>
      {icon}
      {label && (
        <div className="mb-[13px] overflow-hidden text-ellipsis whitespace-nowrap text-[15px] leading-[1.5em] text-black max-[359px]:text-xs">
          {label}
        </div>
      )}
      {children}
    </div>
  );
}

function getTopItemLayoutClasses(index: number, total: number): string {
  const classes: string[] = [];
  const isSecondColumn = index % 2 === 1;
  const isSecondRowOrLater = index >= 2;
  const isLastAndOdd = index === total - 1 && total % 2 === 1;

  if (isSecondColumn) {
    classes.push(
      "before:absolute before:left-0 before:top-[30px] before:block before:h-[100px] before:w-px before:bg-[#ededed] before:content-['']"
    );
  }
  if (isSecondRowOrLater) {
    classes.push(
      "pt-10 after:absolute after:left-0 after:top-5 after:block after:h-px after:w-full after:bg-[#ededed] after:content-['']"
    );
  }
  if (isLastAndOdd) {
    classes.push('w-full');
  }

  return classes.join(' ');
}

const WEIGHT_VARIATION_TEXT =
  'The final invoice amount will be adjusted in case of variation in weight.';

export function ProductDimensions({
  productCode,
  productHeight,
  productWidth,
  productLength,
  productWeight,
  metalWeight,
  diamondCarat,
  noOfDiamonds,
  diamondStoneType,
  solitaireCarat,
  solitaireClarity,
  solitaireColor,
  solitaireTotalNoOfStones,
  gemstones,
  gemstonesTypeAndCount,
  goldPurity,
  metal,
  onSectionViewed,
  onInfoIconClick,
}: ProductDimensionsProps) {
  const viewedRef = useRef(false);
  const { ref: sectionRef, meetsThreshold } = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.2,
    disconnectOnIntersect: true,
  });

  useEffect(() => {
    if (meetsThreshold && !viewedRef.current) {
      viewedRef.current = true;
      onSectionViewed?.();
    }
  }, [meetsThreshold, onSectionViewed]);

  const gemLines = parseProductGemstones(gemstonesTypeAndCount, gemstones);
  const hasGemstones = gemLines.length > 0;
  const showDimensions = Boolean(productHeight || productWidth || productLength);

  const topItems: Array<{
    key: string;
    icon?: ReactNode;
    label?: ReactNode;
    content?: ReactNode;
  }> = [];

  if (goldPurity) {
    topItems.push({
      key: 'gold',
      icon: (
        <DetailIcon
          src={goldIcon}
          className="h-[26px] w-[26px] -mt-1.5"
        />
      ),
      label: (
        <>
          {goldPurity} {getMetalDisplayName(metal)}
        </>
      ),
      content: (
        <DetailValue
          value={metalWeight}
          unit="Gram"
          label="Gold Weight"
          tooltip={WEIGHT_VARIATION_TEXT}
          onInfoIconClick={onInfoIconClick}
        />
      ),
    });
  }

  if (diamondCarat) {
    topItems.push({
      key: 'diamond',
      icon: <DetailIcon src={diamondIcon} className="h-5 w-7" />,
      label: (
        <>
          {noOfDiamonds} {diamondStoneType} Diamonds
        </>
      ),
      content: (
        <DetailValue
          value={formatPdpCarat(diamondCarat)}
          unit="Ct"
          label="Diamond Weight"
          tooltip={WEIGHT_VARIATION_TEXT}
          onInfoIconClick={onInfoIconClick}
        />
      ),
    });
  }

  if (hasGemstones) {
    topItems.push({
      key: 'gemstones',
      icon: (
        <DetailIcon
          src={solitairesIcon}
          className="h-[30px] w-[30px] -mt-[5px]"
        />
      ),
      label: (
        <>
          {gemLines.map((gem, index) => (
            <div key={`${gem.type}-${index}`}>
              {gem.count} {gem.type}
            </div>
          ))}
        </>
      ),
    });
  }

  if (solitaireCarat) {
    topItems.push({
      key: 'solitaire',
      icon: (
        <DetailIcon
          src={solitairesIcon}
          className="h-[30px] w-[30px] -mt-[5px]"
        />
      ),
      label: (
        <>
          {solitaireTotalNoOfStones} {solitaireClarity} {solitaireColor} Solitaires
        </>
      ),
      content: (
        <DetailValue
          value={solitaireCarat}
          unit="Ct"
          label="Solitaires Weight"
          tooltip={WEIGHT_VARIATION_TEXT}
          onInfoIconClick={onInfoIconClick}
        />
      ),
    });
  }

  if (!hasGemstones && productWeight) {
    topItems.push({
      key: 'product-weight',
      content: (
        <DetailValue
          value={productWeight}
          unit="Gram"
          label="Product Weight"
          tooltip={WEIGHT_VARIATION_TEXT}
          onInfoIconClick={onInfoIconClick}
        />
      ),
    });
  }

  if (topItems.length === 0 && !showDimensions) {
    return null;
  }

  return (
    <div ref={sectionRef} className="text-[12px]">
      <div className="mb-5">
        {productCode && (
          <div className="float-right text-xs leading-[22px] text-[#666]">
            Code: {productCode}
          </div>
        )}
        <h3 className="text-sm font-semibold leading-[22px] text-black">Product Details</h3>
      </div>

      <div className="-mx-2.5 after:table after:clear-both after:content-['']">
        {topItems.map((item, index) => (
          <TopDetailItem
            key={item.key}
            className={getTopItemLayoutClasses(index, topItems.length)}
            icon={item.icon}
            label={item.label}
          >
            {item.content}
          </TopDetailItem>
        ))}

        <div className="clear-both" aria-hidden />

        {showDimensions && (
          <div className="clear-both float-none mx-auto mt-5 w-full p-0 text-center">
            <div className="mb-[5px] flex items-center justify-center before:h-px before:flex-1 before:bg-[#ededed] before:content-[''] after:h-px after:flex-1 after:bg-[#ededed] after:content-['']">
              <DetailIcon
                src={toolsIcon}
                className="relative z-[1] mx-3 h-5 w-5 bg-white"
              />
            </div>
            <div className="mb-[13px] overflow-hidden text-ellipsis whitespace-nowrap text-[15px] leading-[1.5em] text-black max-[359px]:text-xs">
              Product Dimension
            </div>
            <div className="flex justify-evenly">
              {Boolean(productHeight) && (
                <div className="flex flex-col items-center text-center">
                  <div className="mb-[5px] inline-block whitespace-normal border-b border-[#ededed] pb-[5px] text-[16.8px] leading-[1em] tracking-normal max-[359px]:text-xs">
                    {productHeight} <span className="text-[13px] tracking-[0.7px]">mm</span>
                  </div>
                  <WeightInfoTooltip
                    label="Height"
                    text="Height can vary in the final product."
                    onInfoClick={onInfoIconClick}
                  />
                </div>
              )}
              {Boolean(productWidth) && (
                <div className="flex flex-col items-center text-center">
                  <div className="mb-[5px] inline-block whitespace-normal border-b border-[#ededed] pb-[5px] text-[16.8px] leading-[1em] tracking-normal max-[359px]:text-xs">
                    {productWidth} <span className="text-[13px] tracking-[0.7px]">mm</span>
                  </div>
                  <WeightInfoTooltip
                    label="Width"
                    text="Width can vary in the final product."
                    onInfoClick={onInfoIconClick}
                  />
                </div>
              )}
              {Boolean(productLength) && (
                <div className="flex flex-col items-center text-center">
                  <div className="mb-[5px] inline-block whitespace-normal border-b border-[#ededed] pb-[5px] text-[16.8px] leading-[1em] tracking-normal max-[359px]:text-xs">
                    {productLength} <span className="text-[13px] tracking-[0.7px]">inches</span>
                  </div>
                  <WeightInfoTooltip
                    label="Length"
                    text="Length can vary in the final product."
                    onInfoClick={onInfoIconClick}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
